import { v4 as uuid } from 'uuid'
import Storage from '~/util/storage'

function commitOrganization(
	commit,
	rootState,
	organization,
	{ activate = false } = {}
) {
	commit('setOrganization', organization)

	const currentOrganizationId =
		rootState?.user?.user?.current_organization?.id
	const shouldHydrateCurrentOrganization =
		organization &&
		(activate ||
			(currentOrganizationId &&
				String(currentOrganizationId) === String(organization.id)))

	if (shouldHydrateCurrentOrganization) {
		commit(
			'user/setCurrentOrganization',
			activate ? withUserScopedDefaults(organization) : organization,
			{ root: true }
		)
	}
}

function withUserScopedDefaults(organization) {
	return {
		...organization,
		permissions: organization?.permissions || [],
		member: organization?.member || null,
		member_id: organization?.member_id || null,
		role_in_organization: organization?.role_in_organization || null
	}
}

export const state = () => {
	return {
		creationRequest: null,
		current: null,
		organizations: [],
		organization: null,
		payment_status: null,
		stripe_status: null,
		trial_ends_at: null,
		trial_ends_on: null,
		trial_started_at: null,
		trial_started_on: null,
		company_size: null,
		days_remaining_in_trial: 0,
		is_in_trial: false,
		is_paid: false,
		has_paid_access: false,
		trial_expired: false
	}
}

export const mutations = {
	setCreationRequest(state, request) {
		state.creationRequest = request
	},
	setOrganization(state, organization) {
		state.current = organization
		state.organization = organization
		state.payment_status = organization?.payment_status
		state.stripe_status = organization?.subscription?.stripe_status || null
		state.trial_ends_at = organization?.trial_ends_at
		state.trial_ends_on = organization?.trial_ends_on
		state.trial_started_at = organization?.trial_started_at
		state.trial_started_on = organization?.trial_started_on
		state.company_size = organization?.company_size
		state.days_remaining_in_trial =
			organization?.days_remaining_in_trial || 0
		state.is_in_trial = organization?.is_in_trial || false
		state.is_paid = organization?.is_paid || false
		state.has_paid_access = organization?.has_paid_access || false
		state.trial_expired = organization?.trial_expired || false
	},

	setOrganizations(state, organizations) {
		state.organizations = organizations
	}
}

export const getters = {
	paymentStatus: state => state.payment_status,
	stripeStatus: state => state.stripe_status,
	isInTrial: state => state.is_in_trial,
	isPaid: state => state.is_paid,
	hasPaidAccess: state => state.has_paid_access,
	trialExpired: state => state.trial_expired,
	daysRemainingInTrial: state => state.days_remaining_in_trial
}

export const actions = {
	async create({ state, commit, dispatch, rootState }, payload) {
		const hasOptions =
			payload &&
			Object.prototype.hasOwnProperty.call(payload, 'organization')
		const organization = hasOptions ? payload.organization : payload
		const redirectTo = hasOptions
			? Object.prototype.hasOwnProperty.call(payload, 'redirectTo')
				? payload.redirectTo
				: '/job-offer/create'
			: '/job-offer/create'

		const userId = rootState.user.user.id
		const storageKey = `organization-creation:${userId}`
		const storedRequestId = Storage.get(storageKey)
		const requestId =
			state.creationRequest?.userId === userId
				? state.creationRequest.id
				: typeof storedRequestId === 'string' &&
				  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
						storedRequestId
				  )
				? storedRequestId
				: uuid()
		commit('setCreationRequest', { userId, id: requestId })
		Storage.set(storageKey, requestId)
		const data = await this.$axios.$post(
			'organization',
			{
				...organization,
				creation_request_id: requestId
			},
			{
				timeout: 10000,
				__skipGlobalErrorToast: true
			}
		)
		commitOrganization(commit, rootState, data, {
			activate: true
		})

		if (redirectTo) {
			await this.$router.push(redirectTo)
			await dispatch('completeCreation')
		}

		return data
	},
	completeCreation({ state, commit }) {
		if (state.creationRequest) {
			Storage.remove(
				`organization-creation:${state.creationRequest.userId}`
			)
			commit('setCreationRequest', null)
		}
	},

	fetch({ commit, rootState }, id) {
		return this.$axios
			.$get('organization/' + id)
			.then(data => {
				commitOrganization(commit, rootState, data)
				return data
			})
			.catch(err => {
				console.error('Failed to fetch organization:', err)
				throw err
			})
	},

	fetchBySlug({ commit, rootState }, slug) {
		return this.$axios.$get(`organization/slug/${slug}`).then(data => {
			commitOrganization(commit, rootState, data)
			return data
		})
	},

	update({ commit, dispatch, rootState }, organization) {
		return this.$axios
			.$put('organization/' + organization.id, organization, {
				__skipGlobalErrorToast: true
			})
			.then(data => {
				commitOrganization(commit, rootState, data)
				dispatch('user/getUser', null, { root: true })
				return data
			})
	},

	invite({ commit, dispatch, rootState }, { organization, email }) {
		return this.$axios
			.$post(`organization/${organization.id}/invite`, { email })
			.then(data => {
				commitOrganization(commit, rootState, data)
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('invitation.sent'),
						message: this.app.i18n.t('invitation.sent-text', {
							email
						}),
						type: 'success'
					},
					{ root: true }
				)
			})
			.catch(() => {})
	},

	uninvite({ commit, dispatch, rootState }, { organization, user }) {
		return this.$axios
			.$get(`organization/${organization.id}/uninvite/${user.id}`)
			.then(data => {
				commitOrganization(commit, rootState, data)
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('organization.member.uninvited'),
						message: this.app.i18n.t(
							'organization.member.uninvited-text',
							{
								user: user.fullName,
								organization: organization.name
							}
						),
						type: 'warning'
					},
					{ root: true }
				)
			})
			.catch(() => {})
	},

	changeUserRole(
		{ commit, dispatch, rootState },
		{ organization, user, role }
	) {
		return this.$axios
			.$get(`organization/${organization.id}/role/${user.id}/${role}`)
			.then(data => {
				commitOrganization(commit, rootState, data)
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t(
							'organization.member.role-changed'
						),
						message: this.app.i18n.t(
							'organization.member.role-changed-text',
							{
								user: user.fullName,
								role: this.app.i18n.t(
									'organization.role.' + role
								)
							}
						),
						type: 'success'
					},
					{ root: true }
				)
			})
	},

	deleteInvitation({ commit, dispatch, rootState }, invitation) {
		return this.$axios
			.$delete(`invitation/${invitation.id}`)
			.then(data => {
				commitOrganization(commit, rootState, data)
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('invitation.deleted'),
						message: this.app.i18n.t('invitation.deleted-text', {
							email: invitation.email
						}),
						type: 'warning'
					},
					{ root: true }
				)
			})
			.catch(() => {})
	},

	acceptInvitation({ commit, dispatch }, invitation) {
		return this.$axios
			.$get(`invitation/${invitation.id}/accept`)
			.then(data => {
				commit('user/setUser', data, { root: true })
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('organization.joined', {
							organization: invitation.organization.name
						}),
						type: 'success'
					},
					{ root: true }
				)
			})
	},

	declineInvitation({ commit, dispatch }, invitation) {
		return this.$axios
			.$get(`invitation/${invitation.id}/decline`)
			.then(data => {
				commit('user/setUser', data, { root: true })
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('invitation.declined', {
							organization: invitation.organization.name
						})
					},
					{ root: true }
				)
			})
	},

	uploadLogo(
		{ commit, dispatch, rootState },
		{ organization, file, onProgress = null, options = {} }
	) {
		return dispatch(
			'media/upload',
			{
				file,
				field: 'logo',
				endpoint: `organization/${organization.id}/logo`,
				onProgress,
				options
			},
			{ root: true }
		).then(data => {
			commitOrganization(commit, rootState, data)
		})
	},

	removeLogo({ commit, rootState }, { organization }) {
		return this.$axios
			.$delete(`organization/${organization.id}/logo`)
			.then(data => {
				commitOrganization(commit, rootState, data)
				return data
			})
	}
}
